import { useState, useRef, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { clearTileSelection, showToast } from '@/features/ui/uiSlice';
import { assignAssetToSections } from '@/features/sections/sectionsSlice';
import { trackEvent } from '@/services/analyticsService';
import { ANALYTICS_EVENTS } from '@/config/analyticsEvents';
import { MOCK_CAMPAIGNS, OPEN_IN_APPS } from '@/config/campaigns';
import styles from './ClipSelectionBar.module.css';

export default function ClipSelectionBar() {
  const dispatch = useAppDispatch();
  const selectedIds = useAppSelector((s) => s.ui.selectedTileIds);
  const sections = useAppSelector((s) => s.sections.library);
  const count = selectedIds.length;

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [campaignMenuOpen, setCampaignMenuOpen] = useState(false);
  const [openInMenuOpen, setOpenInMenuOpen] = useState(false);
  const [campaigns, setCampaigns] = useState(MOCK_CAMPAIGNS);
  const [newCampaignName, setNewCampaignName] = useState('');
  const sendToRef = useRef<HTMLDivElement>(null);

  // Only non-dynamic sections can have assets manually assigned
  const assignableSections = sections.filter((s) => !s.is_dynamic);
  const selectedSection = assignableSections.find((s) => s.id === selectedSectionId);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    if (dropdownOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  useEffect(() => {
    function handleClickOutsideSendTo(e: MouseEvent) {
      if (sendToRef.current && !sendToRef.current.contains(e.target as Node)) {
        setCampaignMenuOpen(false);
        setOpenInMenuOpen(false);
      }
    }
    if (campaignMenuOpen || openInMenuOpen) document.addEventListener('mousedown', handleClickOutsideSendTo);
    return () => document.removeEventListener('mousedown', handleClickOutsideSendTo);
  }, [campaignMenuOpen, openInMenuOpen]);

  if (count === 0) return null;

  const handleAddToCollection = () => {
    if (!selectedSectionId) return;
    trackEvent(ANALYTICS_EVENTS.CLIP_SELECTION_ADD_TO_COLLECTION, { count: selectedIds.length, sectionId: selectedSectionId });
    for (const assetId of selectedIds) {
      dispatch(assignAssetToSections({ assetId, payload: { section_ids: [selectedSectionId] } }));
    }
    dispatch(clearTileSelection());
    setSelectedSectionId(null);
  };

  const handleAssignCampaign = (campaignId: string) => {
    const campaign = campaigns.find((c) => c.id === campaignId);
    if (campaign) {
      trackEvent(ANALYTICS_EVENTS.CAMPAIGN_ASSIGN, { count, campaignId });
      dispatch(showToast({ title: 'Added to campaign', message: `${count} clip${count !== 1 ? 's' : ''} added to "${campaign.label}".` }));
    }
    setCampaignMenuOpen(false);
  };

  const handleCreateCampaign = () => {
    const name = newCampaignName.trim();
    if (!name) return;
    trackEvent(ANALYTICS_EVENTS.CAMPAIGN_CREATE, { count, name });
    setCampaigns((prev) => [
      ...prev,
      { id: `custom-${prev.length}-${name}`, label: name, avatar: name.charAt(0).toUpperCase(), color: '#2E7BF6', assetCount: count, status: 'In production' },
    ]);
    dispatch(showToast({ title: 'Campaign created', message: `"${name}" created and ${count} clip${count !== 1 ? 's' : ''} added.` }));
    setNewCampaignName('');
    setCampaignMenuOpen(false);
  };

  const handleOpenInApp = (label: string) => {
    trackEvent(ANALYTICS_EVENTS.OPEN_IN_APP, { count, app: label });
    dispatch(showToast({ title: 'Opening…', message: `Opening ${count} clip${count !== 1 ? 's' : ''} in ${label}.` }));
    setOpenInMenuOpen(false);
  };

  return (
    <div className={styles.bar} role="toolbar" aria-label="Clip selection actions">
      {/* Left: count */}
      <div className={styles.left}>
        <span className={styles.count}>{count}</span>
        <span className={styles.countLabel}>{count === 1 ? 'clip' : 'clips'} selected</span>
      </div>

      {/* Right: collection selector + action buttons + clear */}
      <div className={styles.right}>
        {/* Collection dropdown */}
        <div className={styles.dropdownWrap} ref={dropdownRef}>
          <button
            className={styles.projectSelect}
            onClick={() => setDropdownOpen((v) => !v)}
            aria-haspopup="listbox"
            aria-expanded={dropdownOpen}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="3" width="20" height="14" rx="2" />
              <path d="M8 21h8M12 17v4" />
            </svg>
            <span className={styles.selectLabel}>{selectedSection ? selectedSection.label : 'Select a collection'}</span>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 4, flexShrink: 0 }}>
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
          {dropdownOpen && (
            <ul className={styles.dropdown} role="listbox" aria-label="Collections">
              {assignableSections.length === 0 && (
                <li className={styles.dropdownEmpty}>No collections available</li>
              )}
              {assignableSections.map((sec) => (
                <li
                  key={sec.id}
                  className={`${styles.dropdownItem} ${sec.id === selectedSectionId ? styles.dropdownItemActive : ''}`}
                  role="option"
                  aria-selected={sec.id === selectedSectionId}
                  onClick={() => { setSelectedSectionId(sec.id); setDropdownOpen(false); }}
                >
                  {sec.label}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Add to collection — not a native `disabled` button: that forces a
            not-allowed/default cursor in every browser regardless of CSS, so
            it wouldn't read as clickable. Instead it's visually disabled via
            aria-disabled + styling, and the click handler itself already
            no-ops until a collection is selected. */}
        <button
          className={`${styles.addBtn} ${selectedSectionId ? styles.addBtnActive : ''}`}
          aria-disabled={!selectedSectionId}
          onClick={handleAddToCollection}
        >
          <span className={styles.addLabel}>Add to Collection</span>
          <svg className={styles.addIcon} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </button>

        {/* Add to campaign / Open in… — same dropdown pattern as the asset
            detail drawer's "send to" row, just scoped to the whole selection. */}
        <div className={styles.sendToRow} ref={sendToRef}>
          <div className={styles.dropdownWrap}>
            <button
              className={`${styles.actionBtn} ${styles.comingSoonHideable}`}
              onClick={() => { setCampaignMenuOpen((v) => !v); setOpenInMenuOpen(false); }}
              aria-haspopup="listbox"
              aria-expanded={campaignMenuOpen}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 11l18-8-8 18-2-8-8-2z" />
              </svg>
              <span className={styles.actionLabel}>Add to campaign</span>
            </button>
            {campaignMenuOpen && (
              <div className={`${styles.dropdown} ${styles.sendToDropdown} ${styles.campaignDropdown}`}>
                <div className={styles.campaignList}>
                  {campaigns.map((c) => (
                    <button key={c.id} className={styles.campaignRow} onClick={() => handleAssignCampaign(c.id)}>
                      <span className={styles.dropdownItemIcon} style={{ background: c.color }}>{c.avatar}</span>
                      <span className={styles.campaignInfo}>
                        <span className={styles.campaignName}>{c.label}</span>
                        <span className={styles.campaignMeta}>{c.assetCount} asset{c.assetCount !== 1 ? 's' : ''} · {c.status}</span>
                      </span>
                    </button>
                  ))}
                </div>
                <div className={styles.campaignCreateRow}>
                  <input
                    className={styles.campaignCreateInput}
                    placeholder="New campaign name…"
                    value={newCampaignName}
                    onChange={(e) => setNewCampaignName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleCreateCampaign(); }}
                  />
                  <button className={styles.campaignCreateBtn} disabled={!newCampaignName.trim()} onClick={handleCreateCampaign}>Add</button>
                </div>
              </div>
            )}
          </div>

          <div className={styles.dropdownWrap}>
            <button
              className={`${styles.actionBtn} ${styles.comingSoonHideable}`}
              onClick={() => { setOpenInMenuOpen((v) => !v); setCampaignMenuOpen(false); }}
              aria-haspopup="listbox"
              aria-expanded={openInMenuOpen}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              <span className={styles.actionLabel}>Open in…</span>
            </button>
            {openInMenuOpen && (
              <div className={`${styles.dropdown} ${styles.sendToDropdown} ${styles.openInDropdown}`}>
                {OPEN_IN_APPS.map((app) => (
                  <button key={app.key} className={styles.openInRow} onClick={() => handleOpenInApp(app.label)}>
                    <span className={styles.openInIcon}>{app.icon}</span>
                    <span className={styles.openInLabel}>{app.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <button
          className={styles.clearBtn}
          onClick={() => { trackEvent(ANALYTICS_EVENTS.CLIP_SELECTION_CLEAR, { count }); dispatch(clearTileSelection()); }}
          aria-label="Clear selection"
        >
          <span className={styles.clearLabel}>Clear</span>
          <svg className={styles.clearIcon} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    </div>
  );
}
